import React from "react";
import {
  DataLoadingWrapper,
  EmptyListContent,
  HeaderBreadcrumbs,
  MultiSectionLayout,
} from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useStopSessionRecording } from "@web-src/modules/common/utils/posthogSetup";
import {
  WhatsappAccountProvider,
  InstagramAccountProvider,
  EmailAccountProvider,
  ClientSupportPageProvider,
} from "./providers";
import { useWhatsappAccount } from "./providers/WhatsappAccountProvider/useWhatsappAccount";
import { useInstagramAccount } from "./providers/InstagramAccountProvider/useInstagramAccount";
import { ConversationListSidebar } from "./components/ConversationListSidebar";
import { InboxSidebar } from "./components/InboxSidebar";
import { ClientSupportChat } from "./components/ClientSupportChat";
import { TicketsView } from "./components/TicketsView";
import { CreateTicketSidebar } from "./components/CreateTicketSidebar";
import { TicketDetailsSidebar } from "./components/TicketDetailsSidebar";
import { ReactComponent as PlatformsIcon } from "./assets/platforms.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ManageAccountsDrawer } from "./components/ManageAccountsDrawer";
import { WebChatBotAccountProvider } from "./providers/WebChatBotAccountProvider";
import { useWebChatBotAccount } from "./providers/WebChatBotAccountProvider/useWebChatBotAccount";
import { useEmailAccount } from "./providers/EmailAccountProvider/useEmailAccount";
import { ManageAccountsDrawerProvider } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerProvider";
import { useManageAccountsDrawer } from "./components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { useClientSupportPageContext } from "./providers/ClientSupportPageProvider/useClientSupportPageContext";

export const InnerClientSupportPage: React.FC = () => {
  useStopSessionRecording();

  const {
    selectedView,
    isConversationSidePanelOpen,
    ticketDetailsSidebarState,
    createTicketSidebarState,
    closeTicketDetailsSidebar,
    closeCreateTicketSidebar,
  } = useClientSupportPageContext();

  const {
    crmConfig,
    isManageAccountDrawerOpen,
    openManageAccountDrawer,
    isCRMConfigError,
    isCRMConfigLoading,
    refetchCRMConfig,
  } = useManageAccountsDrawer();

  const { whatsappAgent } = useWhatsappAccount();

  const { instagramAccount, isConnectingInstagramAccount } =
    useInstagramAccount();

  const { webChatBotAccount, isConnectingWebChatBotAccount } =
    useWebChatBotAccount();

  const { emailAccount, isEmailConfigLoading } = useEmailAccount();

  const { t } = useTranslations();

  return (
    <>
      <DataLoadingWrapper
        isLoading={
          isConnectingInstagramAccount ||
          isConnectingWebChatBotAccount ||
          isCRMConfigLoading ||
          isEmailConfigLoading
        }
        isError={isCRMConfigError}
        onRetry={refetchCRMConfig}
      >
        {(() => {
          if (
            !whatsappAgent &&
            !instagramAccount &&
            !webChatBotAccount &&
            !crmConfig?.twilio?.id &&
            !emailAccount
          ) {
            return (
              <MultiSectionLayout
                header={
                  <HeaderBreadcrumbs
                    items={[
                      {
                        title: t({
                          id: "client-support-page.crm-settings",
                          defaultMessage: "CRM Settings",
                        }),
                      },
                    ]}
                  />
                }
              >
                <EmptyListContent
                  type="custom"
                  className="w-[460px]"
                  customImg={<PlatformsIcon />}
                  customTitle={t({
                    id: "client-support-page.welcome-to-crm-inbox",
                    defaultMessage: "Welcome to CRM Inbox",
                  })}
                  customSubtitle={t({
                    id: "client-support-page.connect-social-media-accounts",
                    defaultMessage:
                      "Connect your social media Accounts to receive and automate the messages from your Clients  🚀",
                  })}
                  customButton={{
                    text: t({
                      id: "client-support-page.add-new-account",
                      defaultMessage: "Add New Account",
                    }),
                    onClick: () =>
                      openManageAccountDrawer({ name: "connectedAccountList" }),
                    iconEnd: <AddIcon />,
                  }}
                />
              </MultiSectionLayout>
            );
          }

          return (
            // keep a min-width floor so the messages window can't be crushed by
            // the sidebars / contact panel; the whole page scrolls horizontally
            // instead of shrinking when the viewport is too narrow
            <div className="jugl__custom-scrollbar h-full w-full overflow-x-auto">
              <MultiSectionLayout
                className={cx(
                  "h-full animate-fade-in",
                  isConversationSidePanelOpen
                    ? "min-w-[1400px]"
                    : "min-w-[1040px]"
                )}
                leftSide={<InboxSidebar />}
              >
                {selectedView.kind === "channel" ? (
                  <MultiSectionLayout
                    className="animate-fade-in"
                    leftSide={<ConversationListSidebar />}
                  >
                    <ClientSupportChat />
                  </MultiSectionLayout>
                ) : (
                  <TicketsView />
                )}
              </MultiSectionLayout>
            </div>
          );
        })()}
      </DataLoadingWrapper>
      <ManageAccountsDrawer
        isOpen={!!isManageAccountDrawerOpen}
        onRequestClose={() => openManageAccountDrawer(undefined)}
        initialScreen={isManageAccountDrawerOpen}
      />
      <TicketDetailsSidebar
        {...ticketDetailsSidebarState}
        onClose={closeTicketDetailsSidebar}
      />
      <CreateTicketSidebar
        {...createTicketSidebarState}
        onClose={closeCreateTicketSidebar}
      />
    </>
  );
};

export const ClientSupportPage: React.FC = () => {
  return (
    <ManageAccountsDrawerProvider isInboxModule>
      <WhatsappAccountProvider>
        <InstagramAccountProvider>
          <WebChatBotAccountProvider>
            <EmailAccountProvider>
              <ClientSupportPageProvider>
                <InnerClientSupportPage />
              </ClientSupportPageProvider>
            </EmailAccountProvider>
          </WebChatBotAccountProvider>
        </InstagramAccountProvider>
      </WhatsappAccountProvider>
    </ManageAccountsDrawerProvider>
  );
};
